import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useClaimSources, useOutputs, usePrenotas, useStream, useVerifications } from 'src/api/queries';
import { ClaimMarker } from 'src/components/editorial/ClaimMarker';
import { ReaderShell } from 'src/components/editorial/ReaderShell';
import { ShareLink } from 'src/components/editorial/ShareLink';
import { Skeleton } from 'src/components/ui/Skeleton';
import { explainCode } from 'src/lib/diagnostics';
import {
  EPISTEMIC_LABEL,
  EPISTEMIC_TONE,
  FINDING_LABEL,
  GATE_LABEL,
  notaProviderLabel,
  OUTPUT_STATUS_LABEL,
  READABILITY_LABEL,
  RISK_LABEL,
  readerConfig,
  readerFurniture,
  readerReview,
} from 'src/lib/reader';
import { buildClaimResolver, type ResolvedClaim } from 'src/lib/resolveClaims';
import { formatDateTime } from 'src/lib/time';
import type {
  EditorialExternalFact,
  EditorialOutput,
  EditorialOutputBodyBlock,
  EditorialSource,
  EditorialVerification,
} from 'src/types/editorial';
import { LoadFailure } from 'src/components/editorial/LoadFailure';

/**
 * The nota as a reader would meet it — set as a page, not as a record.
 *
 * Everything a newspaper puts around a story is here because the pipeline recorded it: the section
 * over the headline is the prenota's category, the dateline is the place the story is about, the
 * backing at the foot is the verification's own sources. None of it is decoration, which is what
 * keeps this a reading surface rather than a skin.
 *
 * It stays honest anyway. Every block still declares whether a claim backs it — in the margin
 * rather than under the paragraph, so the prose runs uninterrupted and the provenance is still
 * one glance away (Art Direction §5, which has no reader exemption).
 */
export function ReaderWorkspace() {
  const { streamId = '', transformationId = '' } = useParams<{ streamId: string; transformationId: string }>();
  const outputs = useOutputs(streamId);
  const stream = useStream(streamId);
  // The prenota supplies the furniture and the verification supplies the backing. Both are one
  // request each and both are already cached by the review screens an editor arrives from.
  const prenotas = usePrenotas(streamId);
  const verifications = useVerifications(streamId);

  // The claim sources are heavy and most readers of a shared link never ask for them, so they load
  // only once someone opens a marker.
  const [wantEvidence, setWantEvidence] = useState(false);
  const claimSources = useClaimSources(streamId, wantEvidence);
  const claimIndex = useMemo(
    () => buildClaimResolver(claimSources.signals, claimSources.prenotas),
    [claimSources.signals, claimSources.prenotas]
  );
  const resolveClaim = (claimId: string) => claimIndex.get(claimId);

  if (outputs.isPending) {
    return (
      <ReaderShell streamId={streamId} output={null} stream={null} comparing={false}>
        <Skeleton variant="document" count={8} />
      </ReaderShell>
    );
  }

  if (outputs.isError) {
    return (
      <ReaderShell streamId={streamId} output={null} stream={null} comparing={false}>
        <LoadFailure title="No se pudo cargar la nota" error={outputs.error} onRetry={outputs.refetch} />
      </ReaderShell>
    );
  }

  const all = outputs.data?.outputs ?? [];
  const output = all.find(item => item.transformationId === transformationId);

  if (!output) {
    return (
      <ReaderShell streamId={streamId} output={null} stream={stream.data?.stream ?? null} comparing={false}>
        <div className="r-empty">
          <strong>No encontramos esta nota</strong>
          La versión ya no existe en esta transmisión. Vuelve al panel y elige otra.
        </div>
      </ReaderShell>
    );
  }

  const siblings = all.filter(item => item.storyId === output.storyId);
  const config = readerConfig(output);
  const content = output.payload.content;
  const prenota = prenotas.data?.prenotas.find(item => item.storyId === output.storyId);
  const furniture = readerFurniture(prenota?.payload);
  const verification = verifications.data?.verifications.find(item => item.storyId === output.storyId);
  const blocks = content?.body_blocks ?? [];
  const proseBlocks = blocks.filter(block => block.type !== 'heading');

  return (
    <ReaderShell streamId={streamId} output={output} stream={stream.data?.stream ?? null} comparing={false}>
      {content ? (
        <>
          <article className="r-article">
            <header className="r-head">
              <p className="r-kicker">
                <span className="r-kicker-section">{furniture.kicker}</span>
                <span className="r-kicker-rule" aria-hidden="true" />
                <span className="r-kicker-profile">
                  {config.label}
                  {config.treatment && ` · ${config.treatment}`}
                </span>
              </p>

              <h2 className="r-headline">{content.headline}</h2>
              {content.deck && <p className="r-deck">{content.deck}</p>}

              <p className="r-byline">
                <span className="r-byline-who">Redacción Pressentia</span>
                <span className="r-byline-sep" aria-hidden="true" />
                <span className="r-byline-meta">
                  perfil <span className="mono">{output.profileId}</span> · {formatDateTime(output.payload.generated_at)}
                  {/* On the page where the note is read as a note, the writer belongs in the byline
                      rather than in a technical footer nobody opens. */}
                  {output.payload.generation?.provider && (
                    <> · escrita con {notaProviderLabel(output.payload.generation.provider, output.payload.generation.model)}</>
                  )}
                </span>
              </p>
            </header>

            {/* The declared stance of the profile that wrote it — the subject of this page, and
                the one surface where naming it is reporting rather than nudging (§14 amendment). */}
            <ProfileRail config={config} />

            <div className="r-body">
              {blocks.map((block, index) => (
                <ReaderBlock
                  key={index}
                  block={block}
                  dateline={index === firstProseIndex(blocks) ? furniture.dateline : ''}
                  dropCap={index === firstProseIndex(blocks) && !furniture.dateline}
                  streamId={streamId}
                  resolve={resolveClaim}
                  onOpen={() => setWantEvidence(true)}
                  loading={claimSources.isPending}
                />
              ))}
              <p className="r-endmark" aria-label="Fin de la nota">
                ■
              </p>
            </div>

            <ReaderBacking verification={verification} output={output} />
            {furniture.topics.length > 0 && (
              <p className="r-filed">
                <span>Archivada en</span>
                {furniture.topics.slice(0, 8).map(topic => (
                  <span key={topic} className="r-filed-topic">
                    {topic}
                  </span>
                ))}
              </p>
            )}
          </article>

          <ReaderSlip output={output} proseCount={proseBlocks.length} />
          <ShareLink
            path={`/nota/${encodeURIComponent(streamId)}/${encodeURIComponent(transformationId)}`}
            label="Comparte esta nota"
          />
          <ReaderOthers output={output} siblings={siblings} streamId={streamId} />
        </>
      ) : (
        <>
          <div className="r-empty">
            <strong>Esta versión no tiene texto</strong>
            {OUTPUT_STATUS_LABEL[output.payload.status] ?? output.payload.status}. El perfil {output.profileId} no
            generó contenido para esta historia.
          </div>
          <ReaderSlip output={output} proseCount={0} />
          <ReaderOthers output={output} siblings={siblings} streamId={streamId} />
        </>
      )}
    </ReaderShell>
  );
}

/** The first paragraph, which is the one that takes the dateline and the initial. */
function firstProseIndex(blocks: EditorialOutputBodyBlock[]): number {
  return blocks.findIndex(block => block.type !== 'heading' && Boolean(block.text));
}

function ProfileRail({ config }: { config: ReturnType<typeof readerConfig> }) {
  return (
    <aside className="r-stance" aria-label="Orientación declarada del perfil">
      <span className="r-stance-label">Orientación declarada</span>
      <span className="r-stance-rail">
        <span className="r-stance-track" />
        <span className="r-stance-mark" style={{ left: `${config.position}%`, background: config.color }} />
      </span>
      <span className="r-stance-scale">
        <span>Izquierda</span>
        <span>Centro</span>
        <span>Derecha</span>
      </span>
      <span className="r-stance-name">
        {config.label}
        {config.intensity && <em> · {config.intensity}</em>}
      </span>
    </aside>
  );
}

function ReaderBlock({
  block,
  dateline,
  dropCap,
  streamId,
  resolve,
  onOpen,
  loading,
}: {
  block: EditorialOutputBodyBlock;
  dateline: string;
  dropCap: boolean;
  streamId: string;
  resolve: (claimId: string) => ResolvedClaim | undefined;
  onOpen: () => void;
  loading: boolean;
}) {
  if (block.type === 'heading') {
    return (
      <div className="r-block r-block--head">
        <h3 className="r-crosshead">{block.text}</h3>
      </div>
    );
  }

  const claimIds = block.claim_ids ?? [];

  return (
    <div className={block.type === 'quote' ? 'r-block r-block--pull' : 'r-block'} data-backed={claimIds.length > 0}>
      {block.type === 'quote' ? (
        <blockquote className="r-pull">{block.text}</blockquote>
      ) : (
        // The initial and the dateline are alternatives, not companions: an initial applied to a
        // paragraph that opens on a place name eats the first letter of the place — "Y UCATÁN".
        // Wire copy has always opened on the dateline instead, which is the rule followed here.
        <p className="r-para" data-dropcap={dropCap || undefined}>
          {dateline && <span className="r-dateline">{dateline} — </span>}
          {block.text}
        </p>
      )}
      {/* In the margin on a wide page, under the paragraph on a narrow one. Either way it is
          always rendered: a reading view that hid unbacked prose would make reading the fastest
          way to approve something without checking it. */}
      <div className="r-margin">
        <ClaimMarker
          claimIds={claimIds}
          streamId={streamId}
          resolve={resolve}
          onOpen={onOpen}
          loading={loading}
        />
      </div>
    </div>
  );
}

/**
 * The backing at the foot of the page: what the note leaned on from outside the broadcast, and
 * what each of those pieces was actually allowed to assert.
 *
 * A reader of a shared link is entitled to this. It is also the only place the epistemic status
 * of a fact appears in a reading surface, and without it a sentence qualified as «reportes
 * periodísticos» looks like a stylistic choice rather than a limit the evidence imposed.
 */
function ReaderBacking({
  verification,
  output,
}: {
  verification: EditorialVerification | undefined;
  output: EditorialOutput;
}) {
  const used = new Set(output.payload.traceability?.used_external_fact_ids ?? []);
  const facts = (verification?.payload.external_facts ?? []).filter(fact =>
    used.size > 0 ? used.has(fact.external_fact_id) : true
  );
  const sources = verification?.payload.sources ?? [];
  if (facts.length === 0 && sources.length === 0) return null;

  const byId = new Map(sources.map(source => [source.source_id, source]));

  return (
    <section className="r-backing">
      <h4 className="r-backing-title">Respaldo externo</h4>
      {facts.length === 0 ? (
        <p className="r-backing-none">
          Esta versión se sostiene solo con lo dicho en la transmisión. Las fuentes de abajo se
          consultaron para la historia, no para una frase de esta nota.
        </p>
      ) : (
        <>
          <ul className="r-facts">
            {facts.slice(0, BACKING_SHOWN).map(fact => (
              <BackingFact key={fact.external_fact_id} fact={fact} sources={byId} />
            ))}
          </ul>
          {/* The rest are real and reachable, but a page is a page: ten paraphrases of one
              position at the foot of a note is a list nobody reads to the end of. */}
          {facts.length > BACKING_SHOWN && (
            <details className="r-backing-more">
              <summary>
                {facts.length - BACKING_SHOWN} hecho{facts.length - BACKING_SHOWN === 1 ? '' : 's'} más de
                respaldo
              </summary>
              <ul className="r-facts">
                {facts.slice(BACKING_SHOWN).map(fact => (
                  <BackingFact key={fact.external_fact_id} fact={fact} sources={byId} />
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {sources.length > 0 && (
        <ul className="r-sources">
          {sources.map(source => (
            <li key={source.source_id}>
              <a href={source.canonical_url} target="_blank" rel="noreferrer noopener">
                {source.title || source.publisher || source.canonical_url}
              </a>
              <span className="r-source-meta">
                {source.publisher}
                {source.retrieved_at && ` · consultada ${source.retrieved_at.slice(0, 10)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** How many pieces of backing the page shows before it asks. */
const BACKING_SHOWN = 5;

function BackingFact({ fact, sources }: { fact: EditorialExternalFact; sources: Map<string, EditorialSource> }) {
  const status = fact.epistemic_status;
  const risk = RISK_LABEL[fact.risk_level ?? 'low'];
  const publishers = fact.source_ids
    .map(id => sources.get(id)?.publisher)
    .filter((publisher): publisher is string => Boolean(publisher));
  const families = fact.source_family_ids?.length ?? 0;

  return (
    <li className="r-fact">
      <p className="r-fact-text">{fact.public_claim_core || fact.statement}</p>
      <p className="r-fact-meta">
        {status && (
          <span className="r-chip" data-tone={EPISTEMIC_TONE[status]}>
            {EPISTEMIC_LABEL[status] ?? status}
          </span>
        )}
        {risk && (
          <span className="r-chip" data-tone="contradicted">
            {risk}
          </span>
        )}
        {fact.stale && (
          <span className="r-chip" data-tone="partial">
            Fuente fuera de vigencia
          </span>
        )}
        {publishers.length > 0 && <span className="r-fact-pub">{publishers.join(' · ')}</span>}
        {/* Independent origins, not urls — the number that says whether corroboration was real. */}
        {families > 0 && (
          <span className="r-fact-pub">
            {families} procedencia{families === 1 ? '' : 's'} independiente{families === 1 ? '' : 's'}
          </span>
        )}
      </p>
      {fact.minimum_public_qualifier && (
        <p className="r-fact-qualifier">Matiz mínimo obligatorio: «{fact.minimum_public_qualifier}»</p>
      )}
    </li>
  );
}

/**
 * The editor's slip. It is set as a slip pinned to the page rather than as more of the page:
 * this is not the piece, it is what stands between the piece and publication, and it sits below
 * because an editor arrives here after reading.
 */
function ReaderSlip({ output, proseCount }: { output: EditorialOutput; proseCount: number }) {
  const review = readerReview(output);
  const validation = output.payload.validation ?? {};
  const gateCodes = Object.keys(validation.evidence_discipline?.codes ?? {});
  const risk = validation.risk;
  const readability = validation.readability?.decision;
  const length = validation.length;
  const cited = (output.payload.content?.body_blocks ?? []).filter(
    block => block.type !== 'heading' && ((block.claim_ids ?? []).length > 0 || (block.fact_ids ?? []).length > 0)
  ).length;

  const hasFigures = proseCount > 0 || Boolean(length?.word_count);
  if (review.isEmpty && gateCodes.length === 0 && !risk?.human_review_required && !hasFigures) return null;

  return (
    <section className="r-slip">
      <h4 className="r-slip-title">Ficha de revisión</h4>

      {hasFigures && (
        <dl className="r-slip-figures">
          <div>
            <dt>Extensión</dt>
            <dd>
              {length?.word_count ?? 0} <span>palabras</span>
            </dd>
          </div>
          <div>
            <dt>Párrafos con respaldo</dt>
            <dd>
              {cited}/{proseCount}
            </dd>
          </div>
          <div>
            <dt>Estilo</dt>
            <dd className="r-slip-word">{READABILITY_LABEL[readability ?? 'not_run'] ?? readability}</dd>
          </div>
          <div>
            <dt>Publicación</dt>
            <dd className="r-slip-word">
              {risk?.human_review_required ? 'Requiere revisión humana' : 'Sin condición de riesgo'}
            </dd>
          </div>
        </dl>
      )}

      {/* Which rule stopped it, not just that something did. */}
      {gateCodes.length > 0 && (
        <>
          <h5>Compuertas que no pasó</h5>
          <ul className="r-slip-list">
            {gateCodes.map(code => (
              <li key={code}>
                <span className="r-chip" data-tone={code.startsWith('STYLE') ? 'partial' : 'contradicted'}>
                  {code}
                </span>{' '}
                {GATE_LABEL[code] ?? 'regla no catalogada'}
              </li>
            ))}
          </ul>
        </>
      )}

      {risk?.human_review_required && (risk.reasons ?? []).length > 0 && (
        <>
          <h5>Por qué la detiene una persona</h5>
          <ul className="r-slip-list">
            {(risk.reasons ?? []).map(reason => (
              <li key={reason}>{RISK_REASON[reason] ?? reason}</li>
            ))}
          </ul>
        </>
      )}

      {validation.readability?.decision === 'discarded' && (
        <p className="r-slip-note">
          La edición de estilo se descartó completa porque movió la evidencia o el grado de certeza
          ({validation.readability.reason}). Se publicó el borrador aprobado, no la versión editada.
        </p>
      )}

      {review.revision.result === 'fixed' && (
        <p className="r-slip-ok">
          Corregida en revisión automática — {review.revision.notes_addressed ?? 0}{' '}
          {review.revision.notes_addressed === 1 ? 'hallazgo resuelto' : 'hallazgos resueltos'} antes de llegar aquí.
        </p>
      )}

      {review.blocking.length > 0 && (
        <>
          <h5>Qué bloquea la publicación</h5>
          <ul className="r-slip-list">
            {review.blocking.map((note, index) => {
              const raw = note.problem ?? note.classification ?? note.field ?? '';
              const label = FINDING_LABEL[raw] ?? (raw ? explainCode(raw).title : 'hallazgo');
              return (
                <li key={index}>
                  <span className="r-chip" data-tone="contradicted" title={raw}>
                    {label}
                  </span>{' '}
                  {note.detail || explainCode(raw).what}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {review.findings.length > 0 && (
        <>
          <h5>Fidelidad factual</h5>
          <ul className="r-slip-list">
            {review.findings.map((finding, index) => (
              <li key={index}>
                <span className="r-chip" data-tone="partial">
                  {FINDING_LABEL[finding.classification] ?? finding.classification}
                </span>{' '}
                {finding.detail ?? ''}
              </li>
            ))}
          </ul>
        </>
      )}

      {review.styleNotes.length > 0 && (
        <>
          <h5>Observaciones de estilo (no bloquean)</h5>
          <ul className="r-slip-list">
            {review.styleNotes.map((deviation, index) => (
              <li key={index}>
                <span className="r-chip" data-tone="unresolved">
                  {deviation.field}
                </span>{' '}
                se pidió “{deviation.expected}”, se obtuvo “{deviation.actual}”
              </li>
            ))}
          </ul>
        </>
      )}

      {review.omitted.length > 0 && (
        <>
          <h5>Claims que la nota dejó fuera</h5>
          <ul className="r-slip-list">
            {review.omitted.map(omission => (
              <li key={omission.claim_id}>
                <span className="r-chip" data-tone="unresolved">
                  omitido
                </span>{' '}
                {omission.reason || omission.claim_id}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

const RISK_REASON: Record<string, string> = {
  criminal_allegation: 'La nota cita una afirmación de materia penal o de daño reputacional grave.',
  attributed_unresolved_lead: 'La entrada se apoya en una atribución que el sistema no pudo cerrar.',
  identity_resolution: 'La identidad de la persona señalada se infirió, no se leyó en el documento.',
};

function ReaderOthers({
  output,
  siblings,
  streamId,
}: {
  output: EditorialOutput;
  siblings: EditorialOutput[];
  streamId: string;
}) {
  const others = siblings.filter(item => item.transformationId !== output.transformationId);
  if (others.length === 0) return null;

  return (
    <section className="r-others">
      <h4>La misma historia, otras voces</h4>
      <p className="r-others-sub">
        Mismos hechos, {others.length + 1} perfiles editoriales.{' '}
        <Link to={`/nota/${encodeURIComponent(streamId)}/${encodeURIComponent(output.transformationId)}/comparar`}>
          Verlas lado a lado →
        </Link>
      </p>
      <div className="r-others-grid">
        {others.map(other => {
          const config = readerConfig(other);
          return (
            <Link
              key={other.transformationId}
              className="r-other"
              to={`/nota/${encodeURIComponent(streamId)}/${encodeURIComponent(other.transformationId)}`}
              data-nav
            >
              <span className="r-other-profile">
                <span className="r-other-dot" style={{ background: config.color }} />
                {config.label}
                {config.treatment && ` · ${config.treatment}`}
              </span>
              <span className="r-other-head">{other.payload.content?.headline ?? 'Sin texto generado'}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
